import { rest, RestRequest } from 'msw';
import {
  createConfirmationCallback,
  createIdpCallback,
  createInputCallback,
  createLoginFailureResponse,
  createMessageCallback,
  createPasswordCallback,
  createRegexPolicy,
  createSessionExpiredResponse,
  createStep,
  createUnauthorizedResponse,
  InputCallbackType,
} from '@mocks';
import { Callback, CallbackType, Step } from '@forgerock/javascript-sdk';
import { Journey } from 'Constants';
import { addressesSearchResponse } from './responses/adressesSearchResponse';
import { headerCartResponse } from './responses/cartResponse';
import { ErrorResponse } from './createError';
import { multipleZipCodeSearchResponse, zipCodeSearchResponse } from './responses/zipCodeSearchResponse';
import { fullForgerockResponseRegisterStep } from './responses/fullForgerockResponseRegisterStep';
import { paypalCallbackResponse } from './responses/paypalCallbackResponse';

const forgeRockSandBoxUrl = 'https://openam-fnac-sandboxeuw1.forgeblocks.com/am/json/realms/root/realms/alpha/authenticate';
const dcomUrl = 'https://ecom.darty.com:8443';
const dcomApiUrl = dcomUrl + '/espace_client/identity/api/v1';
const dcomOppUrl = dcomUrl + '/tunnel_achat/api';
const addressSearchPath = '/addresses-search-by-city';
const zipCodeSearchPath = '/cities/autocomplete';
const addressValidationPath = '/address-validation';
type FieldType = 'input' | 'output';

export const handlers = [
  rest.get(dcomUrl + '/tunnel_achat/api/cart/header', async (req, res, context) =>
    res(context.status(200), context.json(headerCartResponse)),
  ),
  rest.get(dcomApiUrl + addressSearchPath, async (req, res, context) => {
    if (getParam(req, 'input') === '1 RUE TOTO') {
      return res(
        context.status(500),
        context.json({
          status: 500,
          message: 'Internal server error',
          errorCode: 'INTERNAL_SERVER_ERROR',
        }),
      );
    }

    return res(context.status(200), context.json(addressesSearchResponse));
  }),
  rest.get(dcomApiUrl + addressValidationPath, async (req, res, context) => {
    if (getParam(req, 'city') === 'GERIENFE') return res(context.status(200), context.json(false));
    return res(context.status(200), context.json(true));
  }),
  rest.get(dcomOppUrl + zipCodeSearchPath, async (req, res, context) => {
    if (getParam(req, 'zipcode') === '22222') {
      return res(
        context.status(400),
        context.json({
          status: 400,
          message: 'The zipcode input is invalid',
          errorCode: 'INVALID_ZIP_CODE',
        }),
      );
    }

    if (getParam(req, 'zipcode') === '75020') {
      return res(context.status(200), context.json(zipCodeSearchResponse));
    }

    if (getParam(req, 'zipcode') === '33333') {
      return res(context.status(200), context.json([]));
    }
    return res(context.status(200), context.json(multipleZipCodeSearchResponse));
  }),
  rest.post(forgeRockSandBoxUrl, async (req, res, context) => {
    const forgeRockOkResponse = (step: object) => res(context.delay(1000), context.status(200), context.json(step));
    const forgeRockKoResponse = (data: ErrorResponse) => res(context.status(data.code), context.json(data));

    const body = await getBody(req);
    const stage: Stage = body && (body['stage'] as Stage);
    const journey = getParam(req, 'authIndexValue') as Journey;
    const suspendedUuid = getParam(req, 'suspendedUuid');

    if (journey === Journey.LOGIN && !body) {
      return forgeRockOkResponse(
        createStep(
          'Connexion / Registration Page',
          createIdpCallback(),
          createInputCallback(InputCallbackType.TEXT, 'Adresse e-mail', '', '', {
            required: true,
            policies: [
              {
                policyRequirements: ['MATCH_REGEXP'],
                policyId: 'regexpMatches',
                params: {
                  regexp: '^[\\w\\-\\.\\+]+@([\\w\\-]+\\.)+[\\w\\-]{2,4}$',
                },
              },
            ],
          }),
        ),
      );
    }

    if (journey === Journey.RESET_PASSWORD && !body) {
      if (suspendedUuid) return forgeRockOkResponse(createStep('Confirmation Page', createPasswordCallback('Mot de passe', true)));
      return forgeRockOkResponse(createStep('Reset Password Page', createInputCallback(InputCallbackType.TEXT, 'Votre e-mail')));
    }

    switch (stage) {
      case 'Connexion / Registration Page':
        if (extractEmail(body) === 'ciam.darty@yopmail.com') {
          return forgeRockOkResponse(
            createStep(
              'Connexion Page',
              createMessageCallback(extractEmail(body) || ''),
              createPasswordCallback('Votre mot de passe'),
              createInputCallback(InputCallbackType.BOOLEAN, 'Rester connecté'),
              createMessageCallback('Default message'),
              createConfirmationCallback(['pwd', 'otp']),
            ),
          );
        } else if (extractEmail(body) === 'ciam.error@yopmail.com') {
          return forgeRockOkResponse(createStep('Connexion Page', createMessageCallback('An error occured', '2')));
        } else if (extractEmail(body) === 'ciam.expired@yopmail.com') {
          return forgeRockKoResponse(createSessionExpiredResponse());
        } else if (extractEmail(body) === 'mock-json@yopmail.com') {
          return forgeRockOkResponse(fullForgerockResponseRegisterStep);
        } else if (extractEmail(body) === 'paypal-mock-json@yopmail.com') {
          return forgeRockOkResponse(paypalCallbackResponse);
        } else {
          return forgeRockOkResponse(
            createStep(
              'Registration Page',
              createMessageCallback(extractEmail(body) || ''),
              createInputCallback(InputCallbackType.TEXT, 'Mon profil', 'custom_section_profile'),
              createInputCallback(InputCallbackType.TEXT, 'Professionnel', 'custom_professional'),
              createPasswordCallback('Mot de passe', true),
              createInputCallback(InputCallbackType.TEXT, 'Mes coordonnées', 'custom_section_personal_information'),
              createInputCallback(InputCallbackType.TEXT, 'Genre', 'custom_gender'),
              createInputCallback(InputCallbackType.TEXT, 'Nom', 'givenName', '', {
                policies: [createRegexPolicy(/^[0-9a-zA-Z\\sáàâäãåçéèêëíìîïñóòôöõúùûüýÿæœÁÀÂÄÃÅÇÉÈÊËÍÌÎÏÑÓÒÔÖÕÚÙÛÜÝŸÆŒ'\\-]{1,25}$/)],
              }),
              createInputCallback(InputCallbackType.TEXT, 'Prénom', 'sn', '', {
                policies: [createRegexPolicy(/^[0-9a-zA-Z\\sáàâäãåçéèêëíìîïñóòôöõúùûüýÿæœÁÀÂÄÃÅÇÉÈÊËÍÌÎÏÑÓÒÔÖÕÚÙÛÜÝŸÆŒ'\\-]{1,15}$/)],
              }),
              createInputCallback(InputCallbackType.TEXT, 'Date de naissance', 'custom_birthday', '', { required: false }),
              createInputCallback(InputCallbackType.TEXT, 'Numéro de téléphone', 'phoneNumber', '', {
                policies: [createRegexPolicy(/^0[1-79]\d{8}$/)],
              }),
              createInputCallback(InputCallbackType.TEXT, 'Mon adresse', 'custom_section_address'),
              createInputCallback(InputCallbackType.TEXT, 'Adresse', 'postalAddress'),
              createInputCallback(InputCallbackType.TEXT, `Complément d'adresse`, 'custom_address2', '', { required: false }),
              createInputCallback(InputCallbackType.TEXT, 'Code postal', 'postalCode'),
              createInputCallback(InputCallbackType.TEXT, 'Ville', 'city'),
              createInputCallback(InputCallbackType.TEXT, 'Pays', 'country'),
              createInputCallback(InputCallbackType.TEXT, 'Bâtiment', 'custom_darty_building', '', { required: false }),
              createInputCallback(InputCallbackType.TEXT, 'Escalier', 'custom_darty_stairs', '', { required: false }),
              createInputCallback(InputCallbackType.TEXT, 'Étage', 'custom_darty_floor', '', { required: false }),
              createInputCallback(InputCallbackType.TEXT, 'Porte', 'custom_darty_door', '', { required: false }),
              createInputCallback(InputCallbackType.TEXT, 'Mes préférences de contact', 'custom_section_contact_preferences'),
              createInputCallback(InputCallbackType.BOOLEAN, 'Je veux être informé des promos'),
            ),
          );
        }
      case 'Connexion Page':
        if (extractValue(body, CallbackType.PasswordCallback) === 'fake') {
          return forgeRockKoResponse(createLoginFailureResponse());
        } else if (extractValue(body, 'ConfirmationCallback' as CallbackType) === '1') {
          return forgeRockOkResponse(createStep('OTP Page', createPasswordCallback('One Time Password')));
        } else {
          return forgeRockOkResponse(createStep('default', createMessageCallback('Connection ok')));
        }
      case 'Registration Page':
        if (extractEmail(body) === 'ciam.not.created@yopmail.com') {
          return forgeRockKoResponse(createUnauthorizedResponse());
        } else {
          return forgeRockOkResponse(createStep('default', createMessageCallback('Inscription OK')));
        }
      case 'Reset Password Page':
        if (extractEmail(body) === 'ciam.error@yopmail.com') {
          return forgeRockKoResponse(createUnauthorizedResponse());
        } else if (extractEmail(body) === 'ciam.expired@yopmail.com') {
          return forgeRockKoResponse(createSessionExpiredResponse());
        } else {
          return forgeRockOkResponse(createStep('Email sent OK', createMessageCallback('Default message')));
        }
      case 'Confirmation Page':
        return forgeRockOkResponse(createStep('Password update OK', createMessageCallback('Mot de passe mis à jour')));
      case 'OTP Page':
        if (extractValue(body, CallbackType.PasswordCallback) === '11111111') {
          return forgeRockOkResponse(
            createStep(
              'Locked User Page',
              createMessageCallback('ciam.darty@yopmail.com', '0'),
              createMessageCallback('accountLocked', '0'),
              createConfirmationCallback(['reset password', 'no']),
            ),
          );
        } else {
          return forgeRockOkResponse(
            createStep(
              'Retry or resend OTP',
              createMessageCallback('Le code de connexion semble erroné ou expiré. Veuillez réessayer ou demander un nouveau code.', '2'),
              createMessageCallback('Default message', '0'),
              createConfirmationCallback(['retry', 'Send new OTP']),
            ),
          );
        }
      case 'Retry or resend OTP':
        return forgeRockOkResponse(createStep('OTP Page', createPasswordCallback('One Time Password')));
    }

    return forgeRockOkResponse(
      createStep(
        'default',
        createMessageCallback('Mock pas encore implémenté pour cette étape'),
        createConfirmationCallback(['retry', 'Send new OTP']),
      ),
    );
  }),
];

const getBody = (req: RestRequest) =>
  req
    .json()
    .then((json) => json)
    .catch(() => undefined);

const getParam = (req: RestRequest, param: string) => req.url.searchParams.get(param);

const extractEmail = (body: Step) =>
  extractValue(body, CallbackType.StringAttributeInputCallback) || extractValue(body, CallbackType.TextOutputCallback, 'output') || '';

const extractValue = (body: Step, callbackType: CallbackType, fieldType: FieldType = 'input') =>
  body.callbacks
    ?.filter((value: Callback) => value.type === callbackType)
    .map((callback) => (fieldType === 'input' ? callback.input : callback.output))
    .map((input) => input && `${input[0].value}`)
    .pop();
